import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../../utils/AuthContext";
import Pagination from "../../../Pagination";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../../../GASetup/AnalyticsTracker";
import AllAdsList from "../AllAdsList";
import Filter from "./Filters/Filter";
import LoadingAnimation from "../../../Animations/LoadingAnimation";
import { headingFonts, mainButtonStyles } from "../../../../Styles/Styles";

const AdsManagement = () => {
  const [ads, setAds] = useState([]);
  const [totalAds, setTotalAds] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const adsPerPage = 10;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [createdDateOrder, setCreatedDateOrder] = useState("");
  const [priceOrder, setPriceOrder] = useState("");
  const [status, setStatus] = useState("");
  const [offersOrder, setOffersOrder] = useState("");

  // Determine the API endpoint based on the current route
  const isDeletedAdsPage =
    location.pathname === "/admin/deleted-ads-management";
  const apiEndpoint = isDeletedAdsPage
    ? `${process.env.REACT_APP_API_SERVER_BASE_URL}/adManagement/deletedads`
    : `${process.env.REACT_APP_API_SERVER_BASE_URL}/adManagement/allads`;

  // Effect to fetch ads whenever currentPage changes or route changes
  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${apiEndpoint}?page=${currentPage}&limit=${adsPerPage}&search=${searchQuery}&createdDateOrder=${createdDateOrder}&priceOrder=${priceOrder}&status=${status}&offersOrder=${offersOrder}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.status === 401) {
          alert("Unauthorized. Please log in.");
          logout();
          navigate("/get-started");
          return;
        } else if (!response.ok) {
          throw new Error("Failed to fetch ads. Please try again later.");
        }

        const data = await response.json();
        console.log("Fetched ads:", data); // Log fetched data
        setAds(data.ads);
        setTotalAds(data.totalAds);
        setTotalPages(data.totalPages || 1); // Ensure this is set correctly
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [
    currentPage,
    logout,
    navigate,
    createdDateOrder,
    priceOrder,
    status,
    offersOrder,
    debouncedSearchQuery,
    apiEndpoint,
  ]);

  // Set up debounce for search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // Delay 500ms after the user stops typing

    return () => clearTimeout(timer); // Clean up timer on each render
  }, [searchQuery]);

  const handleEdit = (uniqueId) => {
    navigate(`/edit-ad/${uniqueId}`);
  };

  const handleDelete = async (adId) => {
    if (window.confirm("Are you sure you want to delete this ad?")) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/delete/${adId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete ad");
        }

        setAds((prevAds) => prevAds.filter((ad) => ad._id !== adId));
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleMarkAsSoldOrNot = async (uniqueId, isSold) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/mark-as-sold/${uniqueId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isSold }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update ad status");
      }

      setAds((prevAds) =>
        prevAds.map((ad) =>
          ad.uniqueId === uniqueId ? { ...ad, isSold: !isSold } : ad
        )
      );
    } catch (error) {
      setError(error.message);
    }
  };

  const handlePostAd = () => {
    navigate("/post-ad");
  };

  const handleViewOffers = (uniqueId) => {
    navigate(`/offers/${uniqueId}`);
  };

  const handleViewAd = (uniqueId) => {
    navigate(`/ad/${uniqueId}`);
  };

  const handlePageChange = (page) => {
    console.log("Changing to page:", page); // Debug log
    setCurrentPage(page); // Only set the current page
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>
          {isDeletedAdsPage ? "Deleted Ads Management" : "Ads Management"} -
          Listed.ky
        </title>
        <meta
          name="description"
          content={`${
            isDeletedAdsPage ? "Deleted Ads Management" : "Ads Management"
          } - Listed.ky`}
        />
      </Helmet>
      <AnalyticsTracker />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" sx={{ ...headingFonts }}>
          {isDeletedAdsPage
            ? `Deleted Ads Management (${totalAds})`
            : `Ads Management (${totalAds})`}
        </Typography>
        {!isDeletedAdsPage && (
          <Button
            onClick={handlePostAd}
            className="main-button-style"
            sx={{ ...mainButtonStyles }}
          >
            <>
              <span>POST AD</span>
              <AddIcon />
            </>
          </Button>
        )}
      </Box>

      {!isDeletedAdsPage && (
        <Filter
          searchQuery={searchQuery}
          onSearchChange={(e) => setSearchQuery(e.target.value)}
          createdDateOrder={createdDateOrder}
          onCreatedDateOrderChange={setCreatedDateOrder}
          priceOrder={priceOrder}
          onPriceOrderChange={setPriceOrder}
          status={status}
          onStatusChange={setStatus}
          offersOrder={offersOrder}
          onOffersOrderChange={setOffersOrder}
        />
      )}
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <AllAdsList
            ads={ads}
            handleViewAd={handleViewAd}
            handleViewOffers={handleViewOffers}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleMarkAsSoldOrNot={handleMarkAsSoldOrNot}
            isDeletedAdsPage={isDeletedAdsPage}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange} // Pass only page number
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default AdsManagement;
